/**
 * LedgerTable.jsx — v4
 * CHANGES FROM v3:
 *   - Bulk delete: checkboxes on each row + select-all in header
 *   - "Delete Selected" button appears in toolbar when rows are checked
 *   - Single-delete behaviour unchanged
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Download, Upload, FileSpreadsheet, FileText } from 'lucide-react';
import { getTransactions, deleteTransaction, createTransaction, getHeads } from '../../api/yea.api';
import { downloadExcelTemplate, parseExcelImport, downloadLedgerPDF, downloadLedgerCSV } from '../../utils/exportUtils';
import Button from '../../components/common/Button';
import Modal  from '../../components/common/Modal';
import '../accounting/yea.css';

const MONTHS = ['April','May','June','July','August','September','October','November','December','January','February','March'];

export const lastDateKey = (type) => `drusshti_last_date_${type}`;

const LedgerTable = ({ societyId, fyId, type, banks, fyName, societyName, fy }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [txns,          setTxns]          = useState([]);
  const [heads,         setHeads]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [importModal,   setImportModal]   = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importErrors,  setImportErrors]  = useState([]);
  const [importPreview, setImportPreview] = useState([]);
  const [monthFilter,   setMonthFilter]   = useState('');
  const [exporting,     setExporting]     = useState('');

  // ── Bulk delete state ──────────────────────────────────────────────────────
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [bulkDeleting,  setBulkDeleting]  = useState(false);

  const label       = type === 'income' ? 'Income' : 'Expense';
  const memberLabel = type === 'income' ? 'Member' : 'Particulars';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [txnRes, headRes] = await Promise.all([
        getTransactions(societyId, fyId, { type, month: monthFilter || undefined }),
        getHeads(societyId, type),
      ]);

      const toReceiptNum = (r) => parseInt((r.receiptNo || r.voucherNo || '').replace(/\D/g, ''), 10) || 0;
      const sorted = [...(txnRes.data.transactions || [])].sort((a, b) => {
        const dateDiff = new Date(b.date) - new Date(a.date);
        if (dateDiff !== 0) return dateDiff;
        return toReceiptNum(a) - toReceiptNum(b);
      });
      setTxns(sorted);
      setHeads(headRes.data.heads);
      setSelectedIds(new Set()); // clear selection on refresh

      if (sorted.length > 0) {
        const latestDate = new Date(sorted[0].date).toISOString().slice(0, 10);
        localStorage.setItem(lastDateKey(type), latestDate);
      }
    } catch { toast.error(`Failed to load ${label.toLowerCase()} entries`); }
    finally { setLoading(false); }
  }, [societyId, fyId, type, monthFilter, label]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Checkbox helpers ───────────────────────────────────────────────────────
  const allChecked   = txns.length > 0 && selectedIds.size === txns.length;
  const someChecked  = selectedIds.size > 0 && !allChecked;

  const toggleAll = () => {
    if (allChecked) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(txns.map(t => t._id)));
    }
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Bulk delete ────────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected ${label.toLowerCase()} ${selectedIds.size === 1 ? 'entry' : 'entries'}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    let ok = 0, fail = 0;
    for (const id of selectedIds) {
      try { await deleteTransaction(societyId, fyId, id); ok++; }
      catch { fail++; }
    }
    if (fail === 0) toast.success(`Deleted ${ok} ${label.toLowerCase()} ${ok === 1 ? 'entry' : 'entries'}`);
    else toast.error(`Deleted ${ok}, failed ${fail}`);
    setBulkDeleting(false);
    fetchData();
  };

  // ── Single delete (unchanged) ──────────────────────────────────────────────
  const handleDelete = async (txn) => {
    if (!window.confirm(`Delete this ${label.toLowerCase()} entry of ₹${txn.totalAmount}?`)) return;
    try { await deleteTransaction(societyId, fyId, txn._id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleDownloadTemplate = async () => {
    try {
      setExporting('template');
      const headRes = await getHeads(societyId, type);
      const latestHeads = headRes.data.heads;
      if (latestHeads.length === 0) { toast.error('Add heads in Account Heads tab first'); return; }
      await downloadExcelTemplate(latestHeads, type, fyName || 'FY');
      toast.success('Template downloaded');
    } catch { toast.error('Failed to generate template'); }
    finally { setExporting(''); }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) { toast.error('Please upload .xlsx file'); return; }
    setImportLoading(true);
    setImportErrors([]); setImportPreview([]);
    try {
      const latestHeads = (await getHeads(societyId, type)).data.heads;
      const { rows, errors } = await parseExcelImport(file, latestHeads, banks || []);
      setImportErrors(errors); setImportPreview(rows);
    } catch { toast.error('Failed to read file'); }
    finally { setImportLoading(false); }
    e.target.value = '';
  };

  const handleImportConfirm = async () => {
    if (!importPreview.length) return;
    setImportLoading(true);
    let ok = 0, fail = 0;
    for (const row of importPreview) {
      try { await createTransaction(societyId, fyId, { ...row, type }); ok++; }
      catch { fail++; }
    }
    toast.success(`Imported ${ok} entries${fail ? ` (${fail} failed)` : ''}`);
    setImportModal(false); setImportPreview([]); setImportErrors([]);
    fetchData(); setImportLoading(false);
  };

  const handleExportPDF = async () => {
    if (!txns.length) { toast.error('No entries to export'); return; }
    try { setExporting('pdf'); await downloadLedgerPDF(txns, type, fyName || 'FY', societyName || 'Society'); }
    catch { toast.error('PDF export failed'); }
    finally { setExporting(''); }
  };

  const totalAmount = txns.reduce((s,t) => s + t.totalAmount, 0);
  const totalCash   = txns.reduce((s,t) => s + (t.cashAmount||0), 0);
  const totalBank   = txns.reduce((s,t) => s + (t.bankAmount||0), 0);

  return (
    <div className="animate-in" style={{ position: 'relative', paddingBottom: '64px' }}>

      {/* Top toolbar */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem', flexWrap:'wrap', gap:'0.75rem' }}>
        <h2>{label} Ledger</h2>
        <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>

          {/* Delete Selected — only visible when rows are checked */}
          {selectedIds.size > 0 && (
            <Button
              size="sm"
              variant="danger"
              icon={Trash2}
              loading={bulkDeleting}
              onClick={handleBulkDelete}
            >
              Delete Selected ({selectedIds.size})
            </Button>
          )}

          <div className="field" style={{ margin:0, minWidth:130 }}>
            <div className="field-control">
              <select className="field-input" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}>
                <option value="">All Months</option>
                {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <Button size="sm" variant="ghost" icon={FileSpreadsheet} loading={exporting==='template'} onClick={handleDownloadTemplate}>Template</Button>
          <Button size="sm" variant="ghost" icon={Upload} onClick={() => navigate(`/dashboard/society/${societyId}/fy/${fyId}/import/${type}`)}>Import</Button>
          <Button size="sm" variant="ghost" icon={FileText} loading={exporting==='pdf'} onClick={handleExportPDF}>PDF</Button>
          <Button size="sm" variant="ghost" icon={Download} onClick={() => txns.length ? downloadLedgerCSV(txns, type, fyName||'FY') : toast.error('No entries')}>CSV</Button>
          <Button size="sm" icon={Plus} onClick={() => navigate(`/dashboard/society/${societyId}/fy/${fyId}/add/${type}`)}>
            Add {label}
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading
        ? <div className="loading-center"><div className="spinner" /></div>
        : txns.length === 0
          ? (
            <div className="empty-state card">
              <h3>No {label} entries</h3>
              <p>{monthFilter ? `No entries for ${monthFilter}` : 'No entries yet.'}</p>
              <div style={{ display:'flex', gap:'0.75rem', marginTop:'1rem' }}>
                <Button variant="ghost" icon={FileSpreadsheet} onClick={handleDownloadTemplate}>Template</Button>
                <Button icon={Plus} onClick={() => navigate(`/dashboard/society/${societyId}/fy/${fyId}/add/${type}`)}>Add {label}</Button>
              </div>
            </div>
          )
          : (
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <div className="table-wrap">
                <table className="ledger-table">
                  <thead>
                    <tr>
                      {/* Select-all checkbox */}
                      <th style={{ width:36, textAlign:'center', paddingLeft:'0.75rem' }}>
                        <input
                          type="checkbox"
                          checked={allChecked}
                          ref={el => { if (el) el.indeterminate = someChecked; }}
                          onChange={toggleAll}
                          style={{ cursor:'pointer', accentColor:'var(--danger)', width:15, height:15 }}
                        />
                      </th>
                      <th>Date</th>
                      <th>Month</th>
                      <th>{type==='income'?'Receipt No':'Voucher No'}</th>
                      <th>{memberLabel}</th>
                      <th>Narration</th>
                      <th>Heads</th>
                      <th>Bank Ref</th>
                      <th className="amount-cell">Cash (₹)</th>
                      <th className="amount-cell">Bank (₹)</th>
                      <th className="amount-cell">Total (₹)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map(t => (
                      <tr
                        key={t._id}
                        style={{ background: selectedIds.has(t._id) ? 'var(--danger-bg, rgba(240,107,107,0.07))' : undefined }}
                      >
                        {/* Per-row checkbox */}
                        <td style={{ textAlign:'center', paddingLeft:'0.75rem' }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(t._id)}
                            onChange={() => toggleOne(t._id)}
                            style={{ cursor:'pointer', accentColor:'var(--danger)', width:15, height:15 }}
                          />
                        </td>
                        <td style={{ whiteSpace:'nowrap', fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                          {new Date(t.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric',timeZone:'Asia/Kolkata'})}
                        </td>
                        <td><span className="badge badge-subtle">{t.month}</span></td>
                        <td style={{ fontFamily:'monospace', fontSize:'0.78rem', color:'var(--gold)' }}>
                          {type==='income'?(t.receiptNo||'—'):(t.voucherNo||'—')}
                        </td>
                        <td style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>{t.memberName||'—'}</td>
                        <td style={{ fontSize:'0.78rem', color:'var(--text-muted)', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {t.narration||'—'}
                        </td>
                        <td>
                          {t.headEntries.map((h,i) => (
                            <div key={i} style={{ fontSize:'0.78rem', color:'var(--text-secondary)' }}>
                              {h.headName} — ₹{Number(h.amount).toLocaleString('en-IN')}
                            </div>
                          ))}
                        </td>
                        <td style={{ fontSize:'0.75rem', color:'var(--text-muted)', fontFamily:'monospace' }}>
                          {t.paymentSplits?.find(s => s.bankRef)?.bankRef || t.bankRef || '—'}
                        </td>
                        <td className="amount-cell">
                          {(t.cashAmount||0) > 0
                            ? <span className={type==='income'?'positive-amount':'negative-amount'}>{Number(t.cashAmount).toLocaleString('en-IN')}</span>
                            : <span style={{color:'var(--text-dim)'}}>—</span>}
                        </td>
                        <td className="amount-cell">
                          {(t.bankAmount||0) > 0
                            ? <span className={type==='income'?'positive-amount':'negative-amount'}>{Number(t.bankAmount).toLocaleString('en-IN')}</span>
                            : <span style={{color:'var(--text-dim)'}}>—</span>}
                        </td>
                        <td className="amount-cell" style={{ fontWeight:600 }}>
                          {Number(t.totalAmount).toLocaleString('en-IN')}
                        </td>
                        <td>
                          <div style={{ display:'flex', gap:'0.3rem' }}>
                            <Button size="xs" variant="ghost" icon={Pencil}
                              onClick={() => navigate(`/dashboard/society/${societyId}/fy/${fyId}/edit/${type}/${t._id}`)} />
                            <Button size="xs" variant="danger" icon={Trash2} onClick={() => handleDelete(t)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td />
                      <td colSpan={7} style={{ textAlign:'right', paddingRight:'1rem', fontWeight:600, color:'var(--text-muted)', fontSize:'0.75rem', textTransform:'uppercase' }}>
                        Total ({txns.length})
                      </td>
                      <td className="amount-cell">{totalCash.toLocaleString('en-IN')}</td>
                      <td className="amount-cell">{totalBank.toLocaleString('en-IN')}</td>
                      <td className="amount-cell" style={{ color:type==='income'?'var(--success)':'var(--danger)', fontSize:'0.95rem' }}>
                        {totalAmount.toLocaleString('en-IN')}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )
      }

      {/* Sticky floating Add button */}
      <div style={{
        position: 'sticky', bottom: 0, left: 0, right: 0,
        display: 'flex', justifyContent: 'flex-end',
        padding: '12px 0 4px',
        background: 'linear-gradient(to top, var(--bg) 60%, transparent)',
        pointerEvents: 'none',
      }}>
        <div style={{ pointerEvents: 'auto' }}>
          <Button icon={Plus} onClick={() => navigate(`/dashboard/society/${societyId}/fy/${fyId}/add/${type}`)}>
            Add {label}
          </Button>
        </div>
      </div>

      {/* Import Modal */}
      <Modal
        isOpen={importModal} onClose={() => setImportModal(false)}
        title={`Import ${label} Entries`} subtitle="Upload filled Excel template" size="lg"
        footer={
          <>
            <Button variant="subtle" onClick={() => setImportModal(false)}>Cancel</Button>
            {importPreview.length > 0 && (
              <Button loading={importLoading} onClick={handleImportConfirm}>Import {importPreview.length} entries</Button>
            )}
          </>
        }
      >
        <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>
          <div style={{ padding:'1rem', background:'var(--bg-raised)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border-subtle)' }}>
            <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:'0.5rem' }}>Step 1 — Download template</div>
            <Button size="sm" variant="ghost" icon={FileSpreadsheet} loading={exporting==='template'} onClick={handleDownloadTemplate}>Download {label} Template</Button>
          </div>
          <div style={{ padding:'1rem', background:'var(--bg-raised)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border-subtle)' }}>
            <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:'0.75rem' }}>Step 2 — Upload filled template</div>
            <label style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem', padding:'0.55rem 1rem', background:'var(--bg-overlay)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', cursor:'pointer', fontSize:'0.85rem' }}>
              <Upload size={14} />
              {importLoading ? 'Reading...' : 'Choose .xlsx file'}
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display:'none' }} onChange={handleFileSelect} />
            </label>
          </div>
          {importErrors.length > 0 && (
            <div style={{ padding:'0.85rem', background:'var(--danger-bg)', borderRadius:'var(--radius-sm)', border:'1px solid rgba(240,107,107,0.3)' }}>
              <div style={{ fontWeight:600, fontSize:'0.82rem', color:'var(--danger)', marginBottom:'0.4rem' }}>{importErrors.length} errors:</div>
              {importErrors.map((e,i) => <div key={i} style={{ fontSize:'0.78rem', color:'var(--danger)' }}>• {e}</div>)}
            </div>
          )}
          {importPreview.length > 0 && (
            <div>
              <div style={{ fontWeight:600, fontSize:'0.85rem', color:'var(--success)', marginBottom:'0.5rem' }}>✓ {importPreview.length} rows ready to import</div>
              <div style={{ maxHeight:180, overflowY:'auto', background:'var(--bg-raised)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border-subtle)' }}>
                <table className="ledger-table">
                  <thead><tr><th>Date</th><th>Month</th><th>Head</th><th className="amount-cell">Amount</th></tr></thead>
                  <tbody>
                    {importPreview.map((r,i) => (
                      <tr key={i}>
                        <td style={{ fontSize:'0.78rem' }}>{r.date}</td>
                        <td><span className="badge badge-subtle">{r.month}</span></td>
                        <td style={{ fontSize:'0.75rem' }}>{r.headEntries.map(h=>h.headName).join(', ')}</td>
                        <td className="amount-cell">{Number(r.totalAmount).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default LedgerTable;
